import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "input", "panel", "list", "suggestions", "mobileSuggestions", "mobileList", "searchBar",
    "searchIcon", "searchDots", "btn", "btnLabel", "btnSpinner"
  ]
  static values = { url: String }

  connect() {
    this.timeout = null
    this.hideTimeout = null
    this.mobileQuery = window.matchMedia("(max-width: 639px)")
    this.onMobileChange = () => this.hideMobileSuggestions()
    this.mobileQuery.addEventListener("change", this.onMobileChange)
  }

  disconnect() {
    clearTimeout(this.timeout)
    clearTimeout(this.hideTimeout)
    this.mobileQuery.removeEventListener("change", this.onMobileChange)
  }

  isMobile() {
    return this.mobileQuery.matches
  }

  onInput() {
    const query = this.inputTarget.value.trim()
    if (this.isMobile()) this.hideMobileSuggestions()

    if (query.length >= 2) {
      this.setSearching(true)
    } else {
      this.setSearching(false)
    }

    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.fetchResults(query), 280)
  }

  onFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length >= 2) {
      this.fetchResults(query)
    } else if (this.isMobile() && query.length === 0) {
      this.showMobileSuggestions()
    }
  }

  onBlur() {
    this.hideTimeout = setTimeout(() => {
      this.hidePanel()
      this.hideMobileSuggestions()
    }, 200)
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.hidePanel()
      this.hideMobileSuggestions()
    }
  }

  submit(event) {
    event.preventDefault()
    const query = this.inputTarget.value.trim()
    if (query.length > 0) {
      this.setSearching(true)
      window.location.href = `/search?q=${encodeURIComponent(query)}`
    }
  }

  setSearching(active) {
    if (this.hasSearchBarTarget) {
      this.searchBarTarget.classList.toggle("searching", active)
    }
    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.toggle("hidden", active)
    }
    if (this.hasSearchDotsTarget) {
      this.searchDotsTarget.classList.toggle("hidden", !active)
      this.searchDotsTarget.classList.toggle("flex", active)
    }
    if (this.hasBtnTarget) {
      this.btnTarget.classList.toggle("searching", active)
      this.btnTarget.disabled = active
    }
    if (this.hasBtnLabelTarget) {
      this.btnLabelTarget.classList.toggle("hidden", active)
    }
    if (this.hasBtnSpinnerTarget) {
      this.btnSpinnerTarget.classList.toggle("hidden", !active)
      this.btnSpinnerTarget.classList.toggle("flex", active)
    }
  }

  fillQuery(event) {
    event.preventDefault()
    const query = event.currentTarget.dataset.query
    if (!query) return

    clearTimeout(this.hideTimeout)
    this.inputTarget.value = query
    this.hideMobileSuggestions()
    this.fetchResults(query)
    this.inputTarget.focus()
  }

  showMobileSuggestions() {
    if (!this.hasMobileSuggestionsTarget || !this.hasSearchBarTarget) return

    const rect = this.searchBarTarget.getBoundingClientRect()
    this.mobileSuggestionsTarget.style.top = `${rect.bottom + 8}px`
    this.mobileSuggestionsTarget.style.left = `${rect.left}px`
    this.mobileSuggestionsTarget.style.width = `${rect.width}px`
    this.mobileSuggestionsTarget.classList.remove("hidden")
  }

  hideMobileSuggestions() {
    if (this.hasMobileSuggestionsTarget) {
      this.mobileSuggestionsTarget.classList.add("hidden")
    }
  }

  async fetchResults(q) {
    const query = (q || "").trim()
    if (query.length < 2) {
      this.hidePanel()
      this.setSearching(false)
      return
    }

    this.setSearching(true)

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", query)

    const typeInput = this.element.querySelector('input[name="type"]')
    if (typeInput?.value) url.searchParams.set("type", typeInput.value)

    try {
      const res = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!res.ok) {
        this.setSearching(false)
        return
      }
      const data = await res.json()
      this.setSearching(false)
      this.renderResults(data.results || [])
    } catch (_) {
      this.setSearching(false)
      this.hidePanel()
    }
  }

  renderResults(results) {
    clearTimeout(this.hideTimeout)
    if (!results.length) {
      this.hidePanel()
      return
    }

    this.listTarget.innerHTML = results.map((r) => this.resultRowHtml(r)).join("")
    this.panelTarget.classList.remove("hidden")
  }

  onResultClick() {
    this.setSearching(true)
  }

  resultRowHtml(r) {
    const href = typeof r.url === "string" && r.url.startsWith("/") ? r.url : "#"
    const thumb = this.thumbnailHtml(r.thumbnail_url)
    return `
      <a href="${href}" data-action="click->hero-search#onResultClick" class="hero-search-result flex items-center gap-3 px-4 py-3 text-sm">
        ${thumb}
        <div class="min-w-0 flex-1">
          <span class="font-medium block truncate text-[#e8e8f5]">${this.escapeHtml(r.title)}</span>
          <span class="text-xs text-[#7c7ca0]">${this.escapeHtml(r.label)}</span>
        </div>
      </a>
    `
  }

  thumbnailHtml(thumbnailUrl) {
    if (typeof thumbnailUrl === "string" && thumbnailUrl.length > 0) {
      const src = this.escapeHtml(thumbnailUrl)
      return `<img src="${src}" alt="" class="w-10 h-10 rounded object-cover flex-shrink-0 bg-[#1a1a2e]" loading="lazy" />`
    }

    return `<div class="w-10 h-10 rounded flex-shrink-0 bg-gradient-to-br from-purple-600 to-indigo-700" aria-hidden="true"></div>`
  }

  escapeHtml(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }

  hidePanel() {
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("hidden")
    }
    if (this.hasListTarget) {
      this.listTarget.innerHTML = ""
    }
  }
}
